"use client";

/**
 * Notes side of the working view: one-shot AI generation (the entire AI
 * surface — the chat was deliberately cut) + manual TipTap editing.
 * The document lives only in the session store.
 */
import { useState } from "react";
import { useTranslations } from "next-intl";
import { EditorContent, useEditor } from "@tiptap/react";
import { EditorToolbar } from "@/components/notes/editor-toolbar";
import { ExportMenu } from "@/components/flow/export-menu";
import { createNotesExtensions } from "@/lib/notes/tiptap/extensions";
import { generateNotesFromTranscript } from "@/lib/client/generate-notes";
import { generateSampleNotes } from "@/lib/client/sample-notes";
import {
  exportBaseName,
  downloadBlob,
  downloadText,
  notesToDocxBlob,
  notesToMarkdown,
  printNotes,
} from "@/lib/client/exports";
import {
  createSessionAbort,
  getSessionState,
  setNotesDoc,
  setNotesGenerating,
  useSessionState,
} from "@/lib/client/session-store";
import { logError } from "@/lib/logging";

const EMPTY_NOTES_DOC = { type: "doc", content: [{ type: "paragraph" }] };

/** Shared look of the notes focus field (mirrors the staged context field). */
const CONTEXT_INPUT_CLASS =
  "h-10 w-full rounded-[9px] border border-input bg-background px-3 font-mono text-[13px] text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-primary";

/** Skeleton rows for the generating state: [height, width%, topGap]. */
const SKELETON_ROWS: Array<[number, number, boolean?]> = [
  [22, 45],
  [12, 95],
  [12, 88],
  [12, 92],
  [22, 35, true],
  [12, 90],
  [12, 70],
];

export function NotesPanel() {
  const t = useTranslations("Flow");
  // `generating` lives in the session store (notesGenerating) because the
  // workspace layout also reacts to it — the notes rail expands with it.
  const {
    segments,
    notesDoc,
    audioFile,
    isSample,
    notesGenerating,
    transcriptionContext,
  } = useSessionState();
  const [error, setError] = useState<string | null>(null);
  // Steering for the draft, asked again right before every generation.
  // Defaults to the transcription context from the staged screen (the panel
  // mounts with the workspace, after startSession stored it).
  const [notesContext, setNotesContext] = useState(transcriptionContext);
  // Regenerate opens an inline confirm (replace warning + editable focus
  // field) instead of a bare window.confirm.
  const [confirmingRegenerate, setConfirmingRegenerate] = useState(false);
  const baseName = exportBaseName(audioFile?.name);

  const editor = useEditor({
    extensions: createNotesExtensions({
      placeholder: t("notes.placeholder"),
    }),
    content: notesDoc ?? undefined,
    // Required with SSR: render only after mount so server and client agree.
    immediatelyRender: false,
    onUpdate: ({ editor: e }) => setNotesDoc(e.getJSON()),
    editorProps: {
      attributes: {
        class:
          "notes-prose focus:outline-none min-h-[220px] px-[22px] pb-7 pt-[18px] text-left",
      },
    },
  });

  async function handleGenerate() {
    setConfirmingRegenerate(false);
    // Store-owned scope: a start-over or go-home mid-generation aborts this,
    // and the aborted checks below keep the settled result from writing into
    // the reset (or a brand-new) session — re-arming its unload guard.
    const controller = createSessionAbort();
    setError(null);
    setNotesGenerating(true);
    try {
      // The sample session mocks generation locally: no key, no API spend.
      const doc = isSample
        ? await generateSampleNotes()
        : await generateNotesFromTranscript(segments, {
            context: notesContext,
            signal: controller.signal,
          });
      if (controller.signal.aborted) return;
      setNotesDoc(doc);
      editor?.commands.setContent(doc);
    } catch (err) {
      if (controller.signal.aborted) return;
      logError(err, "[flow] Notes generation failed");
      setError(t("notes.generateError"));
    } finally {
      if (!controller.signal.aborted) setNotesGenerating(false);
    }
  }

  function handleStartBlank() {
    setNotesDoc(EMPTY_NOTES_DOC);
    editor?.commands.setContent(EMPTY_NOTES_DOC);
    editor?.commands.focus();
  }

  const hasNotes = notesDoc !== null;

  return (
    <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[14px] border bg-card">
      <div className="flex flex-none items-center justify-between gap-2.5 border-b px-4 py-2.5">
        <h2 className="text-[13px] font-bold tracking-[0.3px]">
          {t("workspace.notes")}
        </h2>
        <div className="flex items-center gap-2">
          {hasNotes && (
            <button
              type="button"
              onClick={() => setConfirmingRegenerate((open) => !open)}
              disabled={notesGenerating || segments.length === 0}
              className="h-[30px] rounded-lg px-3 text-[12.5px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
            >
              {notesGenerating ? t("notes.generating") : t("notes.regenerate")}
            </button>
          )}
          <ExportMenu
            disabled={!hasNotes}
            actions={[
              {
                label: t("export.md"),
                run: () =>
                  downloadText(
                    notesToMarkdown(getSessionState().notesDoc),
                    `${baseName}-notes.md`,
                  ),
              },
              {
                label: t("export.docx"),
                run: async () =>
                  downloadBlob(
                    await notesToDocxBlob(getSessionState().notesDoc),
                    `${baseName}-notes.docx`,
                  ),
              },
              {
                label: t("export.pdf"),
                // The returned promise settles once the print dialog opened
                // (a cancelled dialog is not detectable) — markExported must
                // wait for it, not for the print frame being appended.
                run: () => printNotes(getSessionState().notesDoc),
              },
              {
                label: t("export.copy"),
                run: () =>
                  navigator.clipboard.writeText(
                    notesToMarkdown(getSessionState().notesDoc),
                  ),
                successMessage: t("export.copiedNotes"),
              },
            ]}
          />
        </div>
      </div>

      {error && (
        <p role="alert" className="border-b px-4 py-2 text-[13px] text-primary">
          {error}
        </p>
      )}

      {confirmingRegenerate && (
        <div className="border-b px-4 py-3">
          <p className="mb-2.5 text-[13px] leading-relaxed">
            {t("notes.replaceConfirm")}
          </p>
          <input
            type="text"
            value={notesContext}
            onChange={(e) => setNotesContext(e.target.value)}
            placeholder={t("notes.contextPlaceholder")}
            aria-label={t("notes.contextLabel")}
            maxLength={500}
            autoFocus
            className={CONTEXT_INPUT_CLASS}
          />
          <div className="mt-2.5 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setConfirmingRegenerate(false)}
              className="h-8 rounded-lg border border-input px-3 text-[12.5px] font-medium transition-colors hover:bg-accent"
            >
              {t("notes.cancel")}
            </button>
            <button
              type="button"
              onClick={() => void handleGenerate()}
              className="h-8 rounded-lg bg-primary px-3 text-[12.5px] font-semibold text-primary-foreground transition-[filter] hover:brightness-105"
            >
              {t("notes.regenerate")}
            </button>
          </div>
        </div>
      )}

      {!hasNotes && !notesGenerating && (
        <div className="flex flex-1 flex-col items-center justify-center gap-1.5 p-8 text-center">
          <p className="font-serif text-[22px] italic">
            {t("notes.emptyTitle")}
          </p>
          <p className="mb-3 max-w-[300px] text-[13.5px] leading-relaxed text-muted-foreground">
            {t("notes.emptyHint")}
          </p>
          <div className="mb-[18px] w-full max-w-[320px] text-left">
            <label
              htmlFor="notes-context"
              className="mb-1.5 block text-[12.5px] font-semibold"
            >
              {t("notes.contextLabel")}
            </label>
            <input
              id="notes-context"
              type="text"
              value={notesContext}
              onChange={(e) => setNotesContext(e.target.value)}
              placeholder={t("notes.contextPlaceholder")}
              maxLength={500}
              className={CONTEXT_INPUT_CLASS}
            />
            <p className="mt-1.5 text-xs text-muted-foreground">
              {t("notes.contextHint")}
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-2.5">
            <button
              type="button"
              onClick={() => void handleGenerate()}
              disabled={segments.length === 0}
              className="h-10 whitespace-nowrap rounded-[10px] bg-primary px-[18px] text-[13.5px] font-semibold text-primary-foreground transition-[filter] hover:brightness-105 disabled:pointer-events-none disabled:opacity-50"
            >
              {t("notes.generate")}
            </button>
            <button
              type="button"
              onClick={handleStartBlank}
              className="h-10 whitespace-nowrap rounded-[10px] border border-input px-[18px] text-[13.5px] font-medium transition-colors hover:bg-accent"
            >
              {t("notes.startBlank")}
            </button>
          </div>
        </div>
      )}

      {notesGenerating && (
        <div className="flex flex-1 flex-col gap-[13px] p-[26px_22px]">
          {SKELETON_ROWS.map(([height, width, topGap], i) => (
            <div
              key={i}
              className="animate-[wd-shim_1.4s_ease-in-out_infinite] rounded-md bg-muted"
              style={{
                height,
                width: `${width}%`,
                marginTop: topGap ? 10 : undefined,
                animationDelay: `${i * 0.12}s`,
              }}
            />
          ))}
          <p className="mt-1.5 font-mono text-[11.5px] text-muted-foreground">
            {t("notes.draftingNote")}
          </p>
        </div>
      )}

      <div
        className={`min-h-0 flex-1 overflow-y-auto ${!hasNotes || notesGenerating ? "hidden" : ""}`}
      >
        {editor && (
          <EditorToolbar editor={editor} className="sticky top-0 z-10" />
        )}
        <EditorContent editor={editor} />
      </div>
    </section>
  );
}
